// Shared routing table for notifications — the web port of the mobile
// notification router (mobile `services/notificationRouter.ts`).
//
// Used by:
//   - components/NotificationHandler.tsx (in-tab foreground click / navigate)
//   - netlify/functions/_push-url.js — the SERVER mirror of this table, used to
//     resolve a closed-tab Web Push notification's click target before the
//     payload is sent (standard Web Push since 2026-09-16; no Firebase).
//
// Keep the logic trivial — it must work identically in the browser main thread
// AND in a plain CommonJS Netlify function (no DOM, no React, no imports).
// public/sw.js needs NO twin of this table: the server resolves the url and
// ships it in the push payload, which is what stops the two from drifting.
// _push-url.js does duplicate the cases that can reach a browser — change both
// together, and test/netlify/push-url.test.ts pins them against each other.
//
// ── Role-awareness ──────────────────────────────────────────────────────────
// The same push `type` lands on a different web surface depending on who
// receives it (driver → /driver_app, store → /store_hub, admin/dispatch →
// /orders, …). Two signals drive that:
//   1. `role` argument — the CURRENT user's UserRole. The main thread always
//      has it (currentUser.role) so foreground clicks route precisely.
//   2. `data.role` — an audience hint the Cloud Functions stamp on the
//      multi-audience copies (`'store' | 'admin' | 'driver' | 'sender' |
//      'receiver'`). Because every push is fanned out to a specific set of
//      tokens, this hint (plus `data.storeId`) is enough for the service
//      worker to reach the right surface even without the session role.
// When neither is decisive we fall back to each type's primary audience.

// UserRole string values (mirrors types.ts `enum UserRole`). Kept as literals
// so this table stays identical to the service-worker copy.
const R_OWNER = 'owner';
const R_SUPER_ADMIN = 'super_admin';
const R_ADMIN = 'admin';
const R_DISPATCH = 'dispatch';
const R_STORE = 'store';
const R_DRIVER = 'driver';
const R_MANAGER = 'stock_manager';

// ── Web surfaces (route + query params consumed by the target page) ──────────
const DRIVER_HOME = '/driver_app';                 // deliveries tab: new orders, stock-in, cash
const DRIVER_TRANSFERS = '/driver_app?tab=transfers'; // driver↔driver order transfers
const DRIVER_STORE = '/driver_app?tab=store';      // temp-packer Store tab (embeds StoreHub)
const STORE_HOME = '/store_hub';                   // store order queue
const STORE_STOCK = '/store_hub?tab=stock';        // store stock approvals
const ADMIN_ORDERS = '/orders';                    // OrderManagement
const ADMIN_DASHBOARD = '/dashboard';              // admin/dispatch home
const ADMIN_INVENTORY = '/inventory';              // InventoryDispatch: stock/cash/return approvals
const PRODUCTS = '/products';                       // catalog (admin + manager)
const MESSAGES = '/messages';                       // unified chat surface (all roles but manager)
const TEXT_INBOX = '/text_inbox';                   // forwarded SIM-phone SMS (admin-tier only)
const LOGIN = '/';                                  // unauthenticated app shell shows Login

export interface NotificationData {
  type?: string;
  orderId?: string;
  productId?: string;
  transactionId?: string;
  /** Set on store-targeted pushes; identifies which store the push is about. */
  storeId?: string;
  conversationId?: string;
  channel?: string;
  fromUserId?: string;
  /** WolfChat parity: a group-room message / reaction alert. */
  roomId?: string;
  messageId?: string;
  /** Audience hint stamped by the Cloud Functions on multi-audience copies. */
  role?: string;
  /** Team task board (0091): the task a notification is about. */
  taskId?: string;
}

function isAdminLike(role: string | undefined): boolean {
  return role === R_OWNER || role === R_SUPER_ADMIN || role === R_ADMIN || role === R_DISPATCH;
}

/**
 * Resolve the effective audience for a push. Prefers the recipient's real
 * UserRole; otherwise infers it from the Cloud-Function `data.role` hint (and
 * `data.storeId`). Returns one of 'driver' | 'store' | 'admin' | undefined.
 */
function audienceFor(data: NotificationData, role: string | undefined): string | undefined {
  if (role === R_DRIVER) return R_DRIVER;
  if (role === R_STORE) return R_STORE;
  if (isAdminLike(role)) return R_ADMIN;
  if (role === R_MANAGER) return R_MANAGER;
  // No session role (service-worker path): infer from the payload hints.
  switch (data.role) {
    case R_STORE: return R_STORE;
    case R_DRIVER: return R_DRIVER;
    case R_ADMIN: return R_ADMIN;
    case 'sender':
    case 'receiver': return R_DRIVER; // transfer participants are always drivers
    default:
      // A store-targeted push carries storeId even without an explicit role.
      return data.storeId ? R_STORE : undefined;
  }
}

/**
 * Given a notification `data` payload (and, when known, the current user's
 * UserRole), return the in-app URL to open on click. Always returns a string.
 */
export function buildNotificationUrl(
  data: NotificationData | null | undefined,
  role?: string,
): string {
  if (!data) return LOGIN;
  const { type, orderId, channel, conversationId, fromUserId } = data;
  const aud = audienceFor(data, role);

  switch (type) {
    // ── Team task board (0091): open the task ──────────────────────────────
    case 'task':
      return data.taskId ? `/tasks?task=${encodeURIComponent(data.taskId)}` : '/tasks';

    // ── Messages: order-scoped + direct ────────────────────────────────────
    // Web has ONE Messages page for every role, so these are role-agnostic —
    // except managers, who have no chat surface (drop them on the catalog).
    case 'direct_message':
    // A reaction on a DM lands in the same conversation as the DM itself.
    case 'direct_message_reaction':
      if (role === R_MANAGER) return PRODUCTS;
      if (fromUserId) return `/messages?direct=${encodeURIComponent(fromUserId)}`;
      if (conversationId?.startsWith('direct_')) {
        const parts = conversationId.replace('direct_', '').split('_');
        return `/messages?direct=${encodeURIComponent(parts[0] || '')}`;
      }
      return MESSAGES;

    case 'new_message':
    // Ditto for an order-chat reaction (push-dispatch messageReactionAdded).
    case 'message_reaction':
      if (role === R_MANAGER) return PRODUCTS;
      if (orderId) {
        const ch = channel || 'dispatch';
        return `/messages?order=${encodeURIComponent(orderId)}&channel=${encodeURIComponent(ch)}`;
      }
      return MESSAGES;

    // ── Group rooms (WolfChat parity) ──────────────────────────────────────
    // `?group=` is the deep link pages/Messages.tsx already consumes.
    case 'room_message':
    // _push-rooms.roomReactionCreated — same room, same deep link.
    case 'room_reaction':
      if (role === R_MANAGER) return PRODUCTS;
      return data.roomId ? `${MESSAGES}?group=${encodeURIComponent(data.roomId)}` : MESSAGES;

    // ── Text Inbox: an inbound forwarded SMS (SIM-phone line) ──────────────
    // Only admin-tier sessions ever fire this type, and the page keys threads
    // by number, so the surface alone is enough — the newest thread is on top.
    case 'forwarded_sms':
      // Land IN the thread, not on the inbox list: /text_inbox/<number> is a
      // real route (App.tsx) and the page opens that conversation, exactly as
      // the email and chat alerts open theirs (Umer, 2026-09-17).
      return conversationId ? `${TEXT_INBOX}/${encodeURIComponent(String(conversationId))}` : TEXT_INBOX;

    // ── Order lifecycle (driver-centric, also visible to store / admin) ─────
    case 'order_assigned':
    case 'order_unassigned':
    case 'order_deleted':
    case 'order_pickup_ready':
    case 'order_store_changed':
    case 'order_delivery_reversed':
    case 'order_marked_delivered': // 0105: told to the driver
    case 'order_edited':
      if (aud === R_STORE) return STORE_HOME;
      if (aud === R_ADMIN) return ADMIN_ORDERS;
      return DRIVER_HOME; // driver + unknown default (these fan out to drivers)

    // ── Store-centric order events ──────────────────────────────────────────
    // A driver who receives one of these is packing that store — send them to
    // the Store tab, not their delivery queue.
    case 'order_assigned_to_store':
    case 'order_returning_to_store':
    case 'order_return_confirmed':
    case 'order_store_unassigned':
    case 'order_delivered':
      if (aud === R_ADMIN) return ADMIN_ORDERS;
      if (aud === R_DRIVER) return DRIVER_STORE;
      return STORE_HOME; // store + unknown default (primary audience)

    // ── Order transfers (driver↔driver) ─────────────────────────────────────
    case 'order_transfer_initiated':
    case 'order_transfer_approved':
    case 'order_transfer_cancelled':
      if (aud === R_ADMIN) return ADMIN_ORDERS;
      return DRIVER_TRANSFERS; // driver + unknown default

    // ── Driver-side stock transactions (assignments, driver↔driver, returns) ─
    // On web the driver approves these on the deliveries tab (pendingStock /
    // pendingCash render there), NOT the transfers tab.
    case 'transaction_assign_to_driver':
    case 'transaction_store_to_driver':
    case 'transaction_driver_transfer':
    case 'transaction_approved':
    case 'transaction_rejected':
      if (aud === R_ADMIN) return ADMIN_INVENTORY;
      return DRIVER_HOME; // driver + unknown default

    // ── Store-side stock transactions ───────────────────────────────────────
    case 'transaction_assign_to_store':
    case 'transaction_driver_to_store':
      if (aud === R_ADMIN) return ADMIN_INVENTORY;
      if (aud === R_DRIVER) return DRIVER_STORE; // temp packer approves on Store tab
      return STORE_STOCK; // store + unknown default

    // ── Return approvals ────────────────────────────────────────────────────
    case 'transaction_return':
    case 'transaction_return_store':
      if (aud === R_DRIVER) return DRIVER_HOME;
      if (aud === R_STORE) return STORE_STOCK;
      return ADMIN_INVENTORY; // admin + unknown default (approver surface)

    // ── Cash turn-in ────────────────────────────────────────────────────────
    case 'transaction_cash':
      if (aud === R_DRIVER) return DRIVER_HOME;
      return ADMIN_INVENTORY; // admin + unknown default (dispatch approves)

    // ── Temp-packer assignment ──────────────────────────────────────────────
    case 'temp_packer_assigned':
    case 'temp_packer_unassigned':
      return DRIVER_STORE; // only drivers ever receive these

    // ── Account-level admin actions on this user ────────────────────────────
    case 'user_password_reset':
      return LOGIN; // signed out — land on the login shell
    case 'user_role_changed':
      if (aud === R_DRIVER) return DRIVER_HOME;
      if (aud === R_STORE) return STORE_HOME;
      if (aud === R_MANAGER) return PRODUCTS;
      if (aud === R_ADMIN) return ADMIN_DASHBOARD;
      return LOGIN; // unknown — role default resolves after the app loads

    // ── Admin fan-out events ────────────────────────────────────────────────
    case 'admin_order_created':
      return ADMIN_ORDERS;
    case 'admin_low_stock':
      return PRODUCTS; // admin catalog + manager catalog are the same route
    case 'admin_return_pending':
      return ADMIN_INVENTORY;

    // ── Legacy web type aliases (pre-parity senders) ────────────────────────
    case 'transfer_request':
    case 'transfer_approved':
    case 'transfer_cancelled':
      if (aud === R_ADMIN) return ADMIN_ORDERS;
      return DRIVER_TRANSFERS;
    case 'order_store_assigned':
      if (aud === R_ADMIN) return ADMIN_ORDERS;
      if (aud === R_DRIVER) return DRIVER_STORE;
      return STORE_HOME;

    default:
      // Unknown type — best-effort by audience, else the app shell.
      if (aud === R_STORE) return STORE_HOME;
      if (aud === R_DRIVER) return DRIVER_HOME;
      if (aud === R_ADMIN) return orderId ? ADMIN_ORDERS : ADMIN_DASHBOARD;
      return orderId ? ADMIN_ORDERS : LOGIN;
  }
}

/**
 * In-app URL that lands the given role on the surface where they work THIS
 * order, with `focus=<orderId>` so the page can scroll to and highlight it.
 *
 * Used by the group-chat order-mention modal's Navigate button. Delegates the
 * role→surface decision to `buildNotificationUrl` (via `order_edited`, whose
 * audience mapping is exactly "wherever you deal with this order": admin →
 * /orders, store → /store_hub, driver → /driver_app) rather than restating the
 * table — netlify/functions/_push-url.js already mirrors that table server-side
 * and the copies must not drift.
 *
 * Only /orders consumes `focus` today; the other surfaces ignore the unknown
 * param and simply open their queue, which is what a notification click does.
 */
export function buildOrderFocusUrl(orderId: string, role?: string): string {
  const base = buildNotificationUrl({ type: 'order_edited', orderId }, role);
  // `order_edited` never yields a surface with a query string today; the
  // separator check keeps this correct if that table ever changes.
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}focus=${encodeURIComponent(orderId)}`;
}

/**
 * Stable notification tag for OS-level deduplication. Browsers replace an
 * older notification with the same tag when a new one fires, so the user
 * sees the latest without stacks of duplicates.
 */
export function buildNotificationTag(data: NotificationData | null | undefined): string {
  if (!data) return `wolf-${Date.now()}`;
  const { type, orderId, channel, conversationId, fromUserId, transactionId, storeId } = data;
  const key = orderId || 'unknown';

  switch (type) {
    case 'direct_message':
    case 'direct_message_reaction':
      return `direct-${conversationId || fromUserId || 'unknown'}`;
    case 'new_message':
    case 'message_reaction':
      return `msg-${key}-${channel || 'dispatch'}`;
    case 'room_message':
    case 'room_reaction':
      // One popup per ROOM: a burst in one group collapses to its newest line.
      return `room-${data.roomId || 'unknown'}`;
    case 'forwarded_sms':
      // conversationId carries the customer's number — one notification per
      // thread; a newer text from the same number replaces the older popup.
      return `fsms-${conversationId || 'all'}`;
    case 'email':
      // conversationId is the cached email id (folder-uid): one popup per
      // message; a re-poll never re-announces the same one.
      return `email-${conversationId || 'all'}`;

    case 'order_assigned':
    case 'order_unassigned':
      return `assigned-${key}`;
    case 'order_pickup_ready':
      return `pickup-${key}`;
    case 'order_deleted':
    case 'order_edited':
    case 'order_store_changed':
    case 'order_delivery_reversed':
    case 'order_delivered':
    case 'order_marked_delivered':
      return `status-${key}-${type}`;

    case 'order_transfer_initiated':
    case 'order_transfer_approved':
    case 'order_transfer_cancelled':
    case 'transfer_request':
    case 'transfer_approved':
    case 'transfer_cancelled':
      return `transfer-${key}`;

    case 'order_assigned_to_store':
    case 'order_returning_to_store':
    case 'order_return_confirmed':
    case 'order_store_unassigned':
    case 'order_store_assigned':
      return `store-${key}-${type}`;

    case 'transaction_assign_to_driver':
    case 'transaction_store_to_driver':
    case 'transaction_driver_transfer':
    case 'transaction_assign_to_store':
    case 'transaction_driver_to_store':
    case 'transaction_return':
    case 'transaction_return_store':
    case 'transaction_cash':
    case 'transaction_approved':
    case 'transaction_rejected':
      return `tx-${transactionId || key}-${type}`;

    case 'temp_packer_assigned':
    case 'temp_packer_unassigned':
      return `temp-packer-${storeId || 'unknown'}`;

    case 'user_password_reset':
    case 'user_role_changed':
      return `account-${type}`;

    case 'admin_order_created':
    case 'admin_return_pending':
      return `admin-${type}-${key}`;
    case 'admin_low_stock':
      return `admin-low-stock-${data.productId || 'all'}`;

    default:
      return orderId ? `order-${orderId}` : `wolf-${Date.now()}`;
  }
}
